/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/AboutProject" />
import * as React from "react";

import { getId } from "VSS/Core/Util/Object";
import { Spinner } from "VSSUI/Spinner";

import { announce } from "VSS/Core/Util/Accessibility";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { arrayEquals } from "VSSUI/Util";

import { Constants, PerformanceConstants, TelemetryConstants } from "TFS/Views/ProjectOverviewPage/Constants";
import { AboutProjectActionCreator } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Actions/AboutProjectActionCreator";
import { AboutProjectCard } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/AboutProjectCard";
import { AddDescriptionUpsell } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/AddDescriptionUpsell";
import { ImagePaths, OverviewTabTelemetryConstants, WikiHomePage } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Constants";
import { AboutProjectData, SourceRepositoryTypes } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Models";
import { AboutProjectState, AboutProjectStoreEvents } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Stores/AboutProjectStore";
import { StoresHub } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Stores/StoresHub";
import { getFeatureFlagState, getImageLocation } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Utils";
import * as Resources from "TFS/Views/ProjectOverviewPage/Resources";
import { StoreLoadingState, UpdatingState } from "TFS/Views/ProjectOverviewPage/Shared/Contracts";
import { CurrentUserInfoState } from "TFS/Views/ProjectOverviewPage/Shared/CurrentUserInfoStore";
import { CurrentUserPermission, ProjectBasicData } from "TFS/Views/ProjectOverviewPage/Shared/Models";
import { ProjectBasicInfoState, ProjectBasicInfoStoreEvents } from "TFS/Views/ProjectOverviewPage/Shared/ProjectBasicInfoStore";

export enum PanelDisplayMode {
    hide,
    showInReadMode,
    showInEditMode,
    showSaving,
    showError
}

interface PanelError {
    tagErrorMessage: string | undefined;
    descriptionError: string | undefined;
}

export interface PanelState {
    displayMode: PanelDisplayMode;
    description: string;
    tags: string[];
    repoType: SourceRepositoryTypes | undefined;
    repoId: string | undefined;
    panelError: PanelError;
    invalidTag: string | undefined;
}

export interface IAboutProjectProps {
    storesHub: StoresHub;
    actionCreator: AboutProjectActionCreator;
}

export interface IAboutProjectState {
    aboutProjectData: AboutProjectData | undefined;
    projectBasicData: ProjectBasicData | undefined;
    currentUserPermission: CurrentUserPermission | undefined;
    loadingState: StoreLoadingState;
    allProjectTagsInCollection: string[];
    descriptionError: string | undefined;
    panelState: PanelState;
}

export class AboutProject extends VssComponent<IAboutProjectProps, IAboutProjectState> {
    private _ttiMarked: boolean;
    private _aboutProjectTitleId: string;

    constructor(props: IAboutProjectProps, context: IVssComponentContext) {
        super(props, context);

        this._aboutProjectTitleId = getId("aboutProjectTitleId");
        this._ttiMarked = false;
        const aboutProjectStoreState: AboutProjectState = this.props.storesHub.aboutProjectStore.getState();
        const projectBasicInfoStoreData: ProjectBasicInfoState = this.props.storesHub.projectBasicInfoStore.getState();
        const currentUserInfoStoreState: CurrentUserInfoState = this.props.storesHub.currentUserInfoStore.getState();
        this.state = {
            aboutProjectData: aboutProjectStoreState.aboutProjectData,
            loadingState: this._getLoadingState(),
            projectBasicData: projectBasicInfoStoreData.projectBasicInfo,
            currentUserPermission: currentUserInfoStoreState.currentUserPermission,
            allProjectTagsInCollection: aboutProjectStoreState.allProjectTagsInCollectionData.tags,
            descriptionError: projectBasicInfoStoreData.descriptionError,
            panelState: this._getPanelStateFromStores()
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this.props.storesHub.aboutProjectStore.addChangedListener(this._onAboutProjectChange);
        this.props.storesHub.currentUserInfoStore.addChangedListener(this._onCurrentUserInfoChange);
        this.props.storesHub.projectBasicInfoStore.addChangedListener(this._onProjectBasicInfoChange);
        this.props.storesHub.projectBasicInfoStore.addListener(ProjectBasicInfoStoreEvents.DescriptionUpdateEvent, this._onUpdated);
        this.props.storesHub.aboutProjectStore.addListener(AboutProjectStoreEvents.UpdateEvent, this._onUpdated);

        this._markAddDescriptionUpsellTti();
    }

    public componentDidUpdate(prevProps: IAboutProjectProps, prevState: IAboutProjectState): void {
        super.componentDidUpdate(prevProps, prevState);

        this._markAddDescriptionUpsellTti();
    }

    public componentWillUnmount(): void {
        this.props.storesHub.aboutProjectStore.removeChangedListener(this._onAboutProjectChange);
        this.props.storesHub.currentUserInfoStore.removeChangedListener(this._onCurrentUserInfoChange);
        this.props.storesHub.projectBasicInfoStore.removeChangedListener(this._onProjectBasicInfoChange);
        this.props.storesHub.projectBasicInfoStore.removeListener(ProjectBasicInfoStoreEvents.DescriptionUpdateEvent, this._onUpdated);
        this.props.storesHub.aboutProjectStore.removeListener(AboutProjectStoreEvents.UpdateEvent, this._onUpdated);

        super.componentWillUnmount();
    }

    public render(): JSX.Element | null {
        switch (this.state.loadingState) {
            case StoreLoadingState.Loading:
                return <Spinner />;
            case StoreLoadingState.LoadFailed:
                return null;
            default:
                const aboutProjectData = this.state.aboutProjectData;
                const projectInfo = this.state.projectBasicData;
                if (aboutProjectData && projectInfo) {
                    const showEditOption = this._hasEditPermission();
                    const isLikesEnabled = getFeatureFlagState(this.context.pageContext, "WebAccess.Project.NewOverviewPage.Likes");
                    return (
                        <>
                            {this._shouldShowAddDescriptionUpsell() ? (
                                <AddDescriptionUpsell
                                    showEditOption={showEditOption}
                                    imageLocation={this._getUpsellImageLocation()}
                                    onAddDescriptionClick={this._onAddDescriptionClick}
                                    aboutProjectTitleId={this._aboutProjectTitleId}
                                />
                            ) : (
                                <AboutProjectCard
                                    description={projectInfo.description}
                                    onEditProject={this._onAboutProjectCardEditButtonClick}
                                    showLikes={isLikesEnabled}
                                    projectId={projectInfo.id}
                                    tags={aboutProjectData.tagsData.tags}
                                    projectName={projectInfo.name}
                                    repoId={aboutProjectData.repositoryId}
                                    repoType={aboutProjectData.sourceRepositoryType}
                                    showEditOption={showEditOption}
                                    onMarkTTI={this._markTTI}
                                    aboutProjectTitleId={this._aboutProjectTitleId}
                                />
                            )}
                            {this.state.panelState.displayMode !== PanelDisplayMode.hide && (
                                <WrappedComponent
                                    dependencies={["ms.vss-tfs-web.about-project-panel"]}
                                    wrappedType={"TFS.ProjectOverview.AboutProjectPanel"}
                                    panelState={this.state.panelState}
                                    projectName={projectInfo.name}
                                    showEditOption={showEditOption}
                                    allProjectTagsInCollection={this.state.allProjectTagsInCollection}
                                    onSaveClick={this._onSaveChanges}
                                    onEditClick={this._onAboutProjectPanelEditButtonClick}
                                    onCancelClick={this._onCancelClick}
                                    onOverlayCtaButtonClick={this._onOverlayCtaButtonClick}
                                    onDescriptionChanged={this._onDescriptionChanged}
                                    onTagListChanged={this._onTagListChanged}
                                    onAboutSourceChanged={this._onAboutSourceChanged}
                                />
                            )}
                        </>
                    );
                } else {
                    return null;
                }
        }
    }

    private _getUpsellImageLocation(): string {
        return getImageLocation(this.context.pageContext, ImagePaths.AddDescriptionUpsellPath);
    }

    private _shouldShowAddDescriptionUpsell(): boolean {
        const aboutProjectData = this.state.aboutProjectData;
        const description: string | undefined = this.state.projectBasicData && this.state.projectBasicData.description;
        if (
            !description &&
            aboutProjectData &&
            aboutProjectData.tagsData.tags.length === 0 &&
            !aboutProjectData.repositoryId &&
            !aboutProjectData.sourceRepositoryType
        ) {
            return true;
        }
        return false;
    }

    private _hasEditPermission(): boolean {
        return (
            !!this.state.currentUserPermission &&
            this.state.currentUserPermission.hasProjectLevelEditPermission &&
            this.state.currentUserPermission.hasManageProjectPropertiesPermission
        );
    }

    private _markAddDescriptionUpsellTti(): void {
        if (!this._ttiMarked && this._shouldShowAddDescriptionUpsell() && this._getLoadingState() !== StoreLoadingState.Loading) {
            this._markTTI("AddDescriptionUpsell");
        }
    }

    private _markTTI = (source: string): void => {
        if (!this._ttiMarked) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive(Constants.Area, PerformanceConstants.ProjectOverviewTtiFeatureName, undefined, {
                source
            });
            this._ttiMarked = true;
        }
    };

    private _onUpdated = (): void => {
        const projectBasicInfoStoreState: ProjectBasicInfoState = this.props.storesHub.projectBasicInfoStore.getState();
        const aboutProjectStoreState: AboutProjectState = this.props.storesHub.aboutProjectStore.getState();

        if (
            aboutProjectStoreState.readmeSettingUpdateState === UpdatingState.Failed ||
            projectBasicInfoStoreState.descriptionUpdateState === UpdatingState.Failed ||
            aboutProjectStoreState.projectTagsUpdateState === UpdatingState.Failed
        ) {
            const panelState: PanelState = { ...this.state.panelState };
            const { aboutProjectData } = aboutProjectStoreState;
            const { projectBasicInfo } = projectBasicInfoStoreState;

            if (aboutProjectData && projectBasicInfo) {
                if (aboutProjectStoreState.readmeSettingUpdateState === UpdatingState.Succeeded) {
                    const { repositoryId, sourceRepositoryType } = aboutProjectData;
                    panelState.repoId = repositoryId;
                    panelState.repoType = sourceRepositoryType;
                }

                if (aboutProjectStoreState.projectTagsUpdateState === UpdatingState.Succeeded) {
                    panelState.tags = aboutProjectData.tagsData.tags;
                }

                if (projectBasicInfoStoreState.descriptionUpdateState === UpdatingState.Succeeded) {
                    panelState.description = projectBasicInfo.description;
                }
            }

            panelState.panelError = { ...panelState.panelError, descriptionError: projectBasicInfoStoreState.descriptionError };
            panelState.displayMode = PanelDisplayMode.showError;

            this.setState({
                panelState,
                projectBasicData: projectBasicInfoStoreState.projectBasicInfo,
                aboutProjectData: aboutProjectStoreState.aboutProjectData
            });
        } else if (
            aboutProjectStoreState.readmeSettingUpdateState !== UpdatingState.InProgress &&
            projectBasicInfoStoreState.descriptionUpdateState !== UpdatingState.InProgress &&
            aboutProjectStoreState.projectTagsUpdateState !== UpdatingState.InProgress
        ) {
            announce(Resources.AboutProjectChangesSavedMessage, true);
            this.setState({
                projectBasicData: projectBasicInfoStoreState.projectBasicInfo,
                panelState: this._getPanelStateFromStores(),
                aboutProjectData: aboutProjectStoreState.aboutProjectData
            });
        }

        return;
    };

    private _onAboutProjectChange = (): void => {
        const { aboutProjectData, allProjectTagsInCollectionData } = this.props.storesHub.aboutProjectStore.getState();
        let panelState: PanelState;
        if (aboutProjectData) {
            panelState = {
                ...this.state.panelState,
                tags: aboutProjectData.tagsData.tags,
                repoId: aboutProjectData.repositoryId,
                repoType: aboutProjectData.sourceRepositoryType
            };
        } else {
            panelState = this.state.panelState;
        }

        this.setState({
            loadingState: this._getLoadingState(),
            aboutProjectData: aboutProjectData,
            allProjectTagsInCollection: allProjectTagsInCollectionData.tags,
            panelState
        });
    };

    private _onCurrentUserInfoChange = (): void => {
        const currentUserInfoStoreState: CurrentUserInfoState = this.props.storesHub.currentUserInfoStore.getState();
        this.setState({
            loadingState: this._getLoadingState(),
            currentUserPermission: currentUserInfoStoreState.currentUserPermission
        });
    };

    private _onProjectBasicInfoChange = (): void => {
        const { projectBasicInfo } = this.props.storesHub.projectBasicInfoStore.getState();
        this.setState({
            loadingState: this._getLoadingState(),
            projectBasicData: projectBasicInfo,
            panelState: projectBasicInfo ? { ...this.state.panelState, description: projectBasicInfo.description } : this.state.panelState
        });
    };

    private _getLoadingState = (): StoreLoadingState => {
        const aboutProjectStoreState = this.props.storesHub.aboutProjectStore.getState();
        const projectBasicInfoStoreState = this.props.storesHub.projectBasicInfoStore.getState();
        const currentUserInfoStoreState = this.props.storesHub.currentUserInfoStore.getState();

        if (
            aboutProjectStoreState.loadingState === StoreLoadingState.LoadFailed ||
            projectBasicInfoStoreState.loadingState === StoreLoadingState.LoadFailed ||
            currentUserInfoStoreState.loadingState === StoreLoadingState.LoadFailed
        ) {
            return StoreLoadingState.LoadFailed;
        } else if (
            aboutProjectStoreState.loadingState === StoreLoadingState.Loading ||
            projectBasicInfoStoreState.loadingState === StoreLoadingState.Loading ||
            currentUserInfoStoreState.loadingState === StoreLoadingState.Loading
        ) {
            return StoreLoadingState.Loading;
        } else {
            return StoreLoadingState.Loaded;
        }
    };

    private _getPanelStateFromStores(): PanelState {
        const aboutProjectData = this.props.storesHub.aboutProjectStore.getState().aboutProjectData;
        const { projectBasicInfo, descriptionError } = this.props.storesHub.projectBasicInfoStore.getState();

        return {
            description: projectBasicInfo ? projectBasicInfo.description : "",
            displayMode: PanelDisplayMode.hide,
            invalidTag: undefined,
            panelError: { tagErrorMessage: undefined, descriptionError: descriptionError },
            repoId: aboutProjectData ? aboutProjectData.repositoryId : undefined,
            repoType: aboutProjectData ? aboutProjectData.sourceRepositoryType : undefined,
            tags: aboutProjectData ? aboutProjectData.tagsData.tags : []
        };
    }

    private _onEditProject = (source: string): void => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(OverviewTabTelemetryConstants.Area, OverviewTabTelemetryConstants.AboutProjectPanel, {
            [TelemetryConstants.Event]: "Show_Panel",
            [TelemetryConstants.Source]: source
        });

        this.props.actionCreator.loadAllProjectTagsInCollection();
        this._updatePanelDisplayMode(PanelDisplayMode.showInEditMode);
    };

    private _onAboutProjectCardEditButtonClick = (): void => {
        this._onEditProject("AboutProjectCard_EditButton");
    };

    private _onAboutProjectPanelEditButtonClick = (): void => {
        this._onEditProject("Panel_EditButton");
    };

    private _onAddDescriptionClick = (): void => {
        this._onEditProject("AddDescription_Upsell");
    };

    private _onCancelClick = (): void => {
        this.setState({ panelState: this._getPanelStateFromStores() });
    };

    private _onSaveChanges = (): void => {
        const { description, tags, repoId, repoType } = this.state.panelState;
        const { aboutProjectData } = this.props.storesHub.aboutProjectStore.getState();
        const { projectBasicInfo } = this.props.storesHub.projectBasicInfoStore.getState();

        if (aboutProjectData && projectBasicInfo) {
            const { repositoryId, sourceRepositoryType, tagsData } = aboutProjectData;
            const isDescriptionChanged = projectBasicInfo.description !== description;
            const isReadmeInfoChanged = repositoryId !== repoId || sourceRepositoryType !== repoType;
            const isTagListChanged = !arrayEquals(tagsData.tags, tags, (a, b) => localeIgnoreCaseComparer(a, b) === 0);

            const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent(OverviewTabTelemetryConstants.Area, OverviewTabTelemetryConstants.AboutProjectPanel, {
                [TelemetryConstants.ProjectId]: projectBasicInfo ? projectBasicInfo.id : undefined,
                IsDescriptionChanged: isDescriptionChanged,
                IsTagListChanged: isTagListChanged,
                IsReadmeInfoChanged: isReadmeInfoChanged,
                [TelemetryConstants.Event]: "SaveButtonClick"
            });

            this._updatePanelDisplayMode(PanelDisplayMode.showSaving);

            if (isDescriptionChanged || isTagListChanged || isReadmeInfoChanged) {
                if (isDescriptionChanged) {
                    this.props.actionCreator.saveProjectDescription(description);
                }
                if (isReadmeInfoChanged) {
                    this.props.actionCreator.saveReadmeSetting(this._getReadmeSetting(repoType, repoId));
                }
                if (isTagListChanged) {
                    this.props.actionCreator.saveProjectTags(tags);
                }
                this.props.actionCreator.onSaveChanges(isDescriptionChanged, isReadmeInfoChanged, isTagListChanged);
            } else {
                this._onCancelClick();
            }
        }
    };

    private _onDescriptionChanged = (description: string): void => {
        this.setState({ panelState: { ...this.state.panelState, description } });
    };

    private _onAboutSourceChanged = (repoType?: SourceRepositoryTypes, repoId?: string): void => {
        this.setState({ panelState: { ...this.state.panelState, repoId, repoType } });
    };

    private _onTagListChanged = (tags: string[], errorMessage: string, invalidTag: string): void => {
        const panelError = this.state.panelState.panelError;
        this.setState({
            panelState: {
                ...this.state.panelState,
                tags,
                invalidTag,
                panelError: {
                    ...panelError,
                    tagErrorMessage: errorMessage
                }
            }
        });
    };

    private _getReadmeSetting(repoType: SourceRepositoryTypes | undefined, repoId: string | undefined): string {
        const repoIdSetting = repoId ? repoId : "";
        return repoType === SourceRepositoryTypes.Wiki ? WikiHomePage + ":" + repoIdSetting : repoIdSetting;
    }

    private _onOverlayCtaButtonClick = (): void => {
        const { tagErrorMessage, descriptionError } = this.state.panelState.panelError;
        if (descriptionError || tagErrorMessage) {
            this._updatePanelDisplayMode(PanelDisplayMode.showInEditMode);
        } else {
            this._onSaveChanges();
        }
    };

    private _updatePanelDisplayMode(displayMode: PanelDisplayMode): void {
        this.setState({ panelState: { ...this.state.panelState, displayMode } });
    }
}
